// Client tipado de `retail-cards-business` (SureKeep · pista Retail).
// EJE BACKEND (ARQUITECTURA_FRONT_PROPUESTA.md §7): una función por operationId del OpenAPI.
//
// Alimenta B10 — "Inventario de tarjetas PCF".
//
// El inventario se lleva POR CANTIDAD, no por número: las tarjetas llegan en sobres sellados y
// nadie en la tienda ve el plástico. El modelo es un libro mayor (movimientos) más su saldo.
// Por eso NO existen `getCard` / `cancelCard` / `listStoreCards` — sus rutas responden 404.
//
// TIPOS: de `@fiado/type-kit/bin/retailCards` — el OpenAPI declara todo como `ApiResponse` genérico
// (ver TD-SKF1-011). Las responses de este dominio son `interface` (no `class`): existen solo en
// tipos, así que SIEMPRE `import type`. Los enums sí son valores, por deep-path.
import type { CardBatchResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardBatchResponse';
import type { CardStockResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockResponse';
import type { CardStockMovementResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/CardStockMovementResponse';
import type { StockAlertsResponse } from '@fiado/type-kit/bin/retailCards/dtos/responses/StockAlertsResponse';
import type { RegisterCardBatchRequest } from '@fiado/type-kit/bin/retailCards/dtos/requests/RegisterCardBatchRequest';
import type { WriteOffCardStockRequest } from '@fiado/type-kit/bin/retailCards/dtos/requests/WriteOffCardStockRequest';
import { apiFetch } from '../../client';

const BASE_URL = import.meta.env.VITE_API_RETAIL_CARDS_BASE_URL;

/** Toda llamada de este servicio va a SU base path — no al del rbac. */
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL });
}

function withIdempotency(key?: string): RequestInit {
    return key ? { headers: { 'Idempotency-Key': key } } : {};
}

/** Query string de los dos parámetros de paginación; vacío si no hay ninguno. */
function pageQuery(page: PageParams): string {
    const qs = new URLSearchParams();
    if (page.limit !== undefined) qs.set('limit', String(page.limit));
    if (page.startKey) qs.set('startKey', page.startKey);
    const s = qs.toString();
    return s ? `?${s}` : '';
}

export type {
    CardBatchResponse,
    CardStockResponse,
    CardStockMovementResponse,
    StockAlertsResponse,
    RegisterCardBatchRequest,
    WriteOffCardStockRequest,
};

/** Cursor opaco de la página anterior, tal como se le devuelve al backend en `startKey`. */
export interface PageParams {
    limit?: number;
    startKey?: string;
}

/** Página de lotes ya normalizada: el cursor viaja como string, igual que el del libro mayor. */
export interface CardBatchPage {
    items: CardBatchResponse[];
    count: number;
    nextToken?: string;
}

// ---------------------------------------------------------------------------
// Lotes — la entrada de mercancía
// ---------------------------------------------------------------------------

/**
 * `publicRegisterCardBatch` — POST /retailers/{retailerId}/card-batches.
 * El `storeId` destino viaja DENTRO del body: el lote nace ya asignado a una tienda.
 * `supplierBatchRef` (folio de la remesa) es lo único que distingue dos entradas iguales.
 */
export function registerCardBatch(
    retailerId: string,
    body: RegisterCardBatchRequest,
    idempotencyKey?: string,
): Promise<CardBatchResponse> {
    return call<CardBatchResponse>(`/retailers/${retailerId}/card-batches`, {
        method: 'POST',
        body: JSON.stringify(body),
        ...withIdempotency(idempotencyKey),
    });
}

/**
 * `publicListCardBatches` — GET /retailers/{retailerId}/stores/{storeId}/card-batches.
 *
 * El backend devuelve el cursor como OBJETO (`lastKey`) mientras el del libro mayor ya viene
 * serializado; acá se serializa para que las dos páginas se consuman igual.
 */
export async function listCardBatches(
    retailerId: string,
    storeId: string,
    page: PageParams = {},
): Promise<CardBatchPage> {
    const wire = await call<{
        items: CardBatchResponse[];
        count: number;
        lastKey?: Record<string, unknown>;
    }>(`/retailers/${retailerId}/stores/${storeId}/card-batches${pageQuery(page)}`);
    return {
        items: wire.items,
        count: wire.count,
        nextToken: wire.lastKey ? JSON.stringify(wire.lastKey) : undefined,
    };
}

// ---------------------------------------------------------------------------
// Inventario por cantidad — saldo + libro mayor
// ---------------------------------------------------------------------------

/**
 * `publicGetCardStock` — GET /retailers/{retailerId}/stores/{storeId}/card-stock.
 * Devuelve el saldo vigente más una página de movimientos (`nextToken` ya serializado).
 *
 * Una tienda que nunca recibió un lote responde 404 `CARD_STOCK_NOT_FOUND`: es inventario vacío,
 * no una falla — quien llama lo distingue por el `code`.
 */
export function getCardStock(
    retailerId: string,
    storeId: string,
    page: PageParams = {},
): Promise<CardStockResponse> {
    return call<CardStockResponse>(
        `/retailers/${retailerId}/stores/${storeId}/card-stock${pageQuery(page)}`,
    );
}

/**
 * `publicWriteOffCardStock` — POST /retailers/{retailerId}/stores/{storeId}/card-stock/write-off.
 * El `operationId` del body es la clave de idempotencia de la baja: la genera quien llama, una vez
 * por intento. Reusarla con otra cantidad da 409 `WRITE_OFF_OPERATION_ID_REUSED`.
 */
export function writeOffCardStock(
    retailerId: string,
    storeId: string,
    body: WriteOffCardStockRequest,
    idempotencyKey?: string,
): Promise<CardStockMovementResponse> {
    return call<CardStockMovementResponse>(
        `/retailers/${retailerId}/stores/${storeId}/card-stock/write-off`,
        {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(idempotencyKey),
        },
    );
}

/**
 * `publicGetStockAlerts` — GET /retailers/{retailerId}/stock-alerts.
 * Tiendas bajo el umbral de stock. El umbral es el parámetro `ANTI_FRAUD/card_stock_low_threshold`
 * (default 10) que sirve `loan-config-business` — este lambda ya lo resuelve; el front solo lee.
 */
export function getStockAlerts(retailerId: string): Promise<StockAlertsResponse> {
    return call<StockAlertsResponse>(`/retailers/${retailerId}/stock-alerts`);
}
